import React, { useState, FormEvent, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getBoostErrorMessage, getErrorMessage } from '../types';
import api, { refreshSession } from '../api';
import { User } from '../interfaces/User';
import { useTranslation } from '../i18n';
import { Tooltip } from './Tooltip';
import { Modal } from './Modal';
import { LocationMap, LatLng } from './LocationMap';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import {
  buildGeocodeQuery,
  hasAnyAddressField,
  isCompleteAddress,
  isCompleteLenderAddress,
} from '../utils/addressQuery';
import { COUNTRY_CODES, DEFAULT_COUNTRY, countryLabelKey } from '../utils/countries';
import { PayoutSetupSection, ConnectStatus } from './PayoutSetupSection';
import { SelfBillingAcceptance } from './SelfBillingAcceptance';
import { MojeIdVerificationSection } from './MojeIdVerificationSection';
import { MarketingConsentSection } from './MarketingConsentSection';
import { LevelBadge } from './LevelBadge';
import { FormLabel } from './FormLabel';
import { FormLegend } from './FormLegend';
import { FieldError } from './FieldError';
import { validateFormSubmit } from '../utils/formValidation';
import { isValidPhoneNumberInput, normalizePhoneNumber } from '../utils/phoneNumber';
import { PhoneInput } from './PhoneInput';
import {
  AccountTab,
  DEFAULT_ACCOUNT_TAB,
  parseAccountTab,
} from '../utils/accountRedirect';
import OwnerUnavailablePeriods from './OwnerUnavailablePeriods';
import {
  OfferEligibility,
  OFFER_ISSUE,
  translateOfferIssues,
} from '../offerEligibility';

interface UserSettingsResponse {
  firstName?: string;
  lastName?: string;
  email?: string;
  legalNameLocked?: boolean;
  isVerified?: boolean;
  hasExternalId?: boolean;
  externalIdProvider?: string;
  verifiedAt?: string;
  phoneNumber?: string;
  street?: string;
  houseNumber?: string;
  city?: string;
  country?: string;
  preciseLatitude?: number;
  preciseLongitude?: number;
  approximateLatitude?: number;
  approximateLongitude?: number;
  experiencePoints?: number;
  level?: number;
  marketingConsent?: boolean;
}

interface GeocodePreview {
  latitude: number;
  longitude: number;
}

/** Editable settings fields, compared against the last saved values to detect unsaved changes. */
interface SettingsSnapshot {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  street: string;
  houseNumber: string;
  city: string;
  country: string;
}

interface AccountProps {
  user: User | null;
  setError: (error: string) => void;
  onSessionRefresh: (accessToken: string, refreshToken: string) => void;
  onOfferEligibilityChange?: () => void;
}

export const Account: React.FC<AccountProps> = ({ user, setError, onSessionRefresh, onOfferEligibilityChange }) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // The URL is the single source of truth so account links can switch tabs while we stay mounted.
  const tab: AccountTab = parseAccountTab(searchParams.get('tab'));
  const [newPassword, setNewPassword] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null);
  const [settingsPhoneNumber, setSettingsPhoneNumber] = useState('');
  const [street, setStreet] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [legalNameLocked, setLegalNameLocked] = useState(false);
  const [isVerified, setIsVerified] = useState(user?.isVerified ?? false);
  const [hasExternalId, setHasExternalId] = useState(false);
  const [verifiedAt, setVerifiedAt] = useState<string | null>(null);
  const [reverifying, setReverifying] = useState(false);

  const [savedPrecise, setSavedPrecise] = useState<LatLng | null>(null);
  const [savedApproximate, setSavedApproximate] = useState<LatLng | null>(null);
  const [previewPrecise, setPreviewPrecise] = useState<LatLng | null>(null);
  const [geocodeStatus, setGeocodeStatus] = useState<'idle' | 'loading' | 'ok' | 'error'>('idle');
  const [geocodeError, setGeocodeError] = useState('');
  const [connectStatus, setConnectStatus] = useState<ConnectStatus | null>(null);
  const [offerEligibility, setOfferEligibility] = useState<OfferEligibility | null>(null);
  const [experiencePoints, setExperiencePoints] = useState(0);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [level, setLevel] = useState(1);
  const [boostUsername, setBoostUsername] = useState('');
  const [boosting, setBoosting] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [savedSnapshot, setSavedSnapshot] = useState<SettingsSnapshot | null>(null);
  const geocodeDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const currentSnapshot: SettingsSnapshot = {
    firstName,
    lastName,
    phoneNumber: settingsPhoneNumber,
    street,
    houseNumber,
    city,
    country,
  };
  const isDirty = savedSnapshot !== null
    && (Object.keys(currentSnapshot) as (keyof SettingsSnapshot)[])
      .some(key => savedSnapshot[key] !== currentSnapshot[key]);

  const { confirmOpen, confirmLeave, cancelLeave, guard } =
    useUnsavedChangesGuard(isDirty);

  const fetchOfferEligibility = useCallback(async () => {
    try {
      const res = await api.get<OfferEligibility>('/users/offer-eligibility');
      setOfferEligibility(res.data);
    } catch {
      setOfferEligibility(null);
    }
  }, []);

  const applyTab = useCallback((next: AccountTab) => {
    setMessage('');
    setMessageType(null);
    setSearchParams(current => {
      const params = new URLSearchParams(current);
      if (next === DEFAULT_ACCOUNT_TAB) {
        params.delete('tab');
      } else {
        params.set('tab', next);
      }
      return params;
    }, { replace: true });
  }, [setSearchParams]);

  const revertToSaved = useCallback(() => {
    if (!savedSnapshot) return;
    setFirstName(savedSnapshot.firstName);
    setLastName(savedSnapshot.lastName);
    setSettingsPhoneNumber(savedSnapshot.phoneNumber);
    setStreet(savedSnapshot.street);
    setHouseNumber(savedSnapshot.houseNumber);
    setCity(savedSnapshot.city);
    setCountry(savedSnapshot.country);
    setAddressError('');
  }, [savedSnapshot]);

  /**
   * Asks for confirmation before an action that abandons the form, then drops the pending edits.
   * Dropping them also keeps the browser's own unload warning from stacking on top of our modal
   * when the action is a full-page redirect.
   */
  const confirmDiscard = useCallback((action: () => void) => {
    guard(() => {
      revertToSaved();
      action();
    });
  }, [guard, revertToSaved]);

  // Both tabs edit the same settings, so a switch would carry pending edits into a form where they
  // are invisible.
  const selectTab = useCallback((next: AccountTab) => {
    confirmDiscard(() => applyTab(next));
  }, [confirmDiscard, applyTab]);

  useEffect(() => {
    const reverify = searchParams.get('reverify');
    const externalError = searchParams.get('external_error');
    if (!reverify && !externalError) return;

    const handleReverifyReturn = async () => {
      if (reverify === 'ok') {
        try {
          const tokens = await refreshSession();
          onSessionRefresh(tokens.accessToken, tokens.refreshToken);
        } catch {
          // Settings reload below still reflects DB state; JWT may stay stale until next refresh.
        }
        setSettingsLoaded(false);
        await fetchOfferEligibility();
        onOfferEligibilityChange?.();
        setMessage(t('mojeid.reverify_success'));
        setMessageType('success');
      } else if (reverify === 'unverified') {
        setMessage(t('mojeid.reverify_unverified'));
        setMessageType('error');
      } else if (reverify === 'incomplete_profile') {
        setMessage(t('mojeid.reverify_incomplete_profile'));
        setMessageType('error');
      } else if (reverify === 'underage') {
        setMessage(t('mojeid.reverify_underage'));
        setMessageType('error');
      } else if (reverify === 'fail') {
        setMessage(externalError || t('mojeid.reverify_failed'));
        setMessageType('error');
      } else if (externalError) {
        setMessage(externalError);
        setMessageType('error');
      }
    };

    void handleReverifyReturn();

    searchParams.delete('reverify');
    searchParams.delete('external_error');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, t, onSessionRefresh, fetchOfferEligibility, onOfferEligibilityChange]);

  const applySettings = useCallback((data: UserSettingsResponse) => {
    setFirstName(data.firstName || '');
    setLastName(data.lastName || '');
    setEmail(data.email || '');
    setLegalNameLocked(Boolean(data.legalNameLocked));
    setIsVerified(Boolean(data.isVerified));
    setHasExternalId(Boolean(data.hasExternalId));
    setVerifiedAt(data.verifiedAt || null);
    setSettingsPhoneNumber(data.phoneNumber || '');
    setStreet(data.street || '');
    setHouseNumber(data.houseNumber || '');
    setCity(data.city || '');
    setCountry(DEFAULT_COUNTRY);
    setExperiencePoints(data.experiencePoints ?? 0);
    setLevel(data.level ?? 1);
    setMarketingConsent(Boolean(data.marketingConsent));
    if (data.preciseLatitude != null && data.preciseLongitude != null) {
      setSavedPrecise({ lat: data.preciseLatitude, lng: data.preciseLongitude });
    } else {
      setSavedPrecise(null);
    }
    if (data.approximateLatitude != null && data.approximateLongitude != null) {
      setSavedApproximate({ lat: data.approximateLatitude, lng: data.approximateLongitude });
    } else {
      setSavedApproximate(null);
    }
    setPreviewPrecise(null);
    setGeocodeStatus('idle');
    setGeocodeError('');
    setSavedSnapshot({
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      phoneNumber: data.phoneNumber || '',
      street: data.street || '',
      houseNumber: data.houseNumber || '',
      city: data.city || '',
      country: DEFAULT_COUNTRY,
    });
  }, []);

  const validateAddress = useCallback(async () => {
    if (!hasAnyAddressField(street, houseNumber, city)) {
      setPreviewPrecise(null);
      setGeocodeStatus('idle');
      setGeocodeError('');
      return;
    }

    if (!isCompleteAddress(street, houseNumber, city, country)) {
      setPreviewPrecise(null);
      setGeocodeStatus('error');
      setGeocodeError(t('account.address_incomplete'));
      return;
    }

    const query = buildGeocodeQuery(street, houseNumber, city, country);
    if (!query) return;

    setGeocodeStatus('loading');
    setGeocodeError('');
    try {
      const res = await api.get<GeocodePreview>('/mapy/geocode', { params: { query } });
      setPreviewPrecise({ lat: res.data.latitude, lng: res.data.longitude });
      setGeocodeStatus('ok');
    } catch (err) {
      setPreviewPrecise(null);
      setGeocodeStatus('error');
      setGeocodeError(getErrorMessage(err, t('account.geocode_failed')));
    }
  }, [street, houseNumber, city, country, t]);

  useEffect(() => {
    if (!settingsLoaded) return;
    if (geocodeDebounceRef.current) clearTimeout(geocodeDebounceRef.current);
    geocodeDebounceRef.current = setTimeout(() => {
      validateAddress();
    }, 600);
    return () => {
      if (geocodeDebounceRef.current) clearTimeout(geocodeDebounceRef.current);
    };
  }, [street, houseNumber, city, country, settingsLoaded, validateAddress]);

  const startReverify = useCallback((silent: boolean) => {
    confirmDiscard(async () => {
      setReverifying(true);
      try {
        const res = await api.post<{ authorizationUrl: string }>(
          `/auth/external/mojeid/reverify/start?silent=${silent}`
        );
        window.location.href = res.data.authorizationUrl;
      } catch (err) {
        setError(getErrorMessage(err, t('mojeid.reverify_failed')));
        setReverifying(false);
      }
    });
  }, [confirmDiscard, setError, t]);

  const handleChangePassword = useCallback(async (e: FormEvent) => {
    if (!validateFormSubmit(e)) return;
    e.preventDefault();
    const id = targetUserId || user?.id;
    try {
      await api.put(`/auth/change-password/${id}`, { newPassword });
      setNewPassword('');
      setTargetUserId('');
      setMessage(t('account.password_success'));
      setMessageType('success');
    } catch (err) {
      setError(getErrorMessage(err, t('account.password_error')));
    }
  }, [newPassword, targetUserId, user, setError, t]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await api.get<UserSettingsResponse>('/users/settings');
        applySettings(res.data);
        setSettingsLoaded(true);
      } catch (err) {
        setError(getErrorMessage(err, t('account.settings_load_error')));
      }
    };

    if (!settingsLoaded) {
      loadSettings();
    }
    fetchOfferEligibility();
  }, [settingsLoaded, setError, t, applySettings, fetchOfferEligibility]);

  /**
   * Shared by both tab forms: the settings endpoint is a full replace and always requires phone
   * and legal name, so the whole body is sent from state no matter which form was submitted.
   */
  const handleSaveSettings = useCallback(async (e: FormEvent, requireAddress = false) => {
    e.preventDefault();
    if (!validateFormSubmit(e)) return;
    setAddressError('');

    // Phone and legal name live on the Settings tab, so their `required` attributes cannot fire
    // when the lender form is submitted. Send the user there instead of showing a raw 400.
    const missingContact = !isValidPhoneNumberInput(settingsPhoneNumber)
      || (!legalNameLocked && (!firstName.trim() || !lastName.trim()));
    if (requireAddress && missingContact) {
      // applyTab resets the banner, so the message has to be queued after it. The guard is skipped
      // on purpose: this switch keeps the pending edits instead of discarding them.
      applyTab('settings');
      setMessage(t('account.owner_contact_missing'));
      setMessageType('error');
      return;
    }

    if (requireAddress && !isCompleteLenderAddress(street, houseNumber, city, country)) {
      setAddressError(t('account.address_required'));
      return;
    }

    if (
      hasAnyAddressField(street, houseNumber, city) &&
      (!isCompleteAddress(street, houseNumber, city, country) || geocodeStatus === 'error')
    ) {
      setAddressError(geocodeError || t('account.address_incomplete'));
      return;
    }

    try {
      const body: Record<string, unknown> = {
        phoneNumber: normalizePhoneNumber(settingsPhoneNumber),
        street: street || null,
        houseNumber: houseNumber || null,
        city: city || null,
        country: country || null,
      };
      if (!legalNameLocked) {
        body.firstName = firstName.trim();
        body.lastName = lastName.trim();
      }
      const res = await api.put<UserSettingsResponse>('/users/settings', body);
      applySettings(res.data);
      await fetchOfferEligibility();
      onOfferEligibilityChange?.();
      setMessage(t('account.settings_success'));
      setMessageType('success');
    } catch (err) {
      setError(getErrorMessage(err, t('account.settings_error')));
    }
  }, [
    city,
    country,
    firstName,
    houseNumber,
    lastName,
    legalNameLocked,
    geocodeError,
    geocodeStatus,
    setError,
    settingsPhoneNumber,
    street,
    t,
    applySettings,
    fetchOfferEligibility,
    onOfferEligibilityChange,
    applyTab,
  ]);

  const showSavedMap = savedPrecise || savedApproximate;
  const showPreviewMap = previewPrecise && geocodeStatus === 'ok';
  const offerIssues = offerEligibility?.issues ?? [];
  const hasContactIssue = offerIssues.includes(OFFER_ISSUE.ContactIncomplete);
  // Only block once eligibility is actually known, so a failed fetch can't lock the user out.
  const payoutStartBlockedMessage = offerEligibility && hasContactIssue
    ? t('connect.start_blocked_incomplete_settings')
    : (connectStatus === null || !connectStatus.selfBillingAccepted
      ? t('connect.start_blocked_self_billing') : undefined);

  const handleBoost = async (e: FormEvent) => {
    e.preventDefault();
    const username = boostUsername.trim();
    if (!username) return;

    setBoosting(true);
    setMessage('');
    setMessageType(null);
    setError('');
    try {
      const res = await api.post<{ amountGranted: number }>('/users/boost', { username });
      setMessage(t('account.boost_success', { amount: res.data.amountGranted, username }));
      setMessageType('success');
      setBoostUsername('');
      const settingsRes = await api.get<UserSettingsResponse>('/users/settings');
      applySettings(settingsRes.data);
    } catch (err) {
      setMessage(getBoostErrorMessage(err, t));
      setMessageType('error');
    } finally {
      setBoosting(false);
    }
  };

  return (
    <div>
      <h2 className="section-title">{t('account.title')}</h2>

      <div className="account-tabs">
        <button className={tab === 'settings' ? 'active' : ''} onClick={() => selectTab('settings')}>
          {t('account.tab_settings')}
        </button>
        <button className={tab === 'owner' ? 'active' : ''} onClick={() => selectTab('owner')}>
          {t('account.tab_owner')}
        </button>
        <button className={tab === 'availability' ? 'active' : ''} onClick={() => selectTab('availability')}>
          {t('account.tab_availability')}
        </button>
        <button className={tab === 'password' ? 'active' : ''} onClick={() => selectTab('password')}>
          {t('account.tab_password')}
        </button>
        <button className={tab === 'experience' ? 'active' : ''} onClick={() => selectTab('experience')}>
          {t('account.tab_experience')}
        </button>
      </div>

      {message && (
        <div className={`alert ${messageType === 'error' ? 'alert-error' : 'alert-success'}`}>
          {message}
        </div>
      )}

      {tab === 'password' && (
        <div className="card">
          <form onSubmit={handleChangePassword}>
            {user?.isAdmin && (
              <div className="form-group">
                <label>
                  {t('account.target_user')}{' '}
                  <Tooltip content={t('account.target_user_help')} />
                </label>
                <input type="text" placeholder="User ID" value={targetUserId} onChange={e => setTargetUserId(e.target.value)} />
              </div>
            )}
            <div className="form-group">
              <FormLabel required>{t('account.new_password')}</FormLabel>
              <input type="password" placeholder={t('account.new_password_placeholder')} value={newPassword} onChange={e => setNewPassword(e.target.value)} required aria-required="true" />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">{t('account.update_password')}</button>
            </div>
          </form>
        </div>
      )}

      {tab === 'settings' && (
        <>
          <MojeIdVerificationSection
            isVerified={isVerified}
            hasExternalId={hasExternalId}
            verifiedAt={verifiedAt}
            reverifying={reverifying}
            loading={!settingsLoaded}
            onStartReverify={startReverify}
          />
          <form onSubmit={e => handleSaveSettings(e)}>
            <div className="card">
              <FormLegend />
              <div className="form-group">
                <FormLabel required={!legalNameLocked}>
                  {t('account.first_name')}
                  {legalNameLocked && (
                    <>
                      {' '}
                      <Tooltip content={t('account.legal_name_locked_help')} />
                    </>
                  )}
                </FormLabel>
                <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} disabled={legalNameLocked} required={!legalNameLocked} aria-required={!legalNameLocked} />
              </div>
              <div className="form-group">
                <FormLabel required={!legalNameLocked}>{t('account.last_name')}</FormLabel>
                <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} disabled={legalNameLocked} required={!legalNameLocked} aria-required={!legalNameLocked} />
              </div>
              <div className="form-group">
                <label>{t('account.email')}</label>
                <input type="text" value={email} disabled />
              </div>
              <div className="form-group">
                <FormLabel required>{t('account.settings_phone_number')}</FormLabel>
                <PhoneInput
                  placeholder={t('account.settings_phone_placeholder')}
                  value={settingsPhoneNumber}
                  onChange={setSettingsPhoneNumber}
                  required
                  aria-required="true"
                  invalidMessage={t('validation.phone_invalid')}
                />
              </div>
            </div>
            <div className="form-actions" style={{ marginTop: '1rem' }}>
              <button type="submit" className="btn btn-primary">
                {t('account.settings_save')}
              </button>
            </div>
          </form>
          <MarketingConsentSection consent={marketingConsent} loading={!settingsLoaded} />
        </>
      )}

      {tab === 'experience' && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>{t('account.experience_title')}</h3>
          <p style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 1rem' }}>
            <LevelBadge level={level} size="md" />
            <span>{t('account.experience_summary', { level, xp: experiencePoints })}</span>
          </p>
          <p className="text-muted" style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
            {t('account.boost_description')}
          </p>
          <form onSubmit={handleBoost}>
            <div className="form-group">
              <label>{t('account.boost_username')}</label>
              <input
                type="text"
                value={boostUsername}
                onChange={e => setBoostUsername(e.target.value)}
                placeholder={t('account.boost_username_placeholder')}
                disabled={boosting}
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-secondary" disabled={boosting || !boostUsername.trim()}>
                {boosting && <span className="btn-spinner" aria-hidden="true" />}
                {t('account.boost_submit')}
              </button>
            </div>
          </form>
        </div>
      )}

      {tab === 'owner' && (
        <form onSubmit={e => handleSaveSettings(e, true)}>
          <div className="card">
            <p className="text-muted" style={{ marginTop: 0 }}>{t('account.owner_settings_hint')}</p>
            {offerEligibility && !offerEligibility.eligible && (
              <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
                <div>
                  <strong>{t('offer_eligibility.account_summary_title')}</strong>
                  <p style={{ margin: '0.5rem 0 0' }}>{t('offer_eligibility.account_summary_intro')}</p>
                  <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
                    {translateOfferIssues(offerIssues, t).filter((value)=>value).map(issue => (
                      <li key={issue}>{issue}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {hasContactIssue && (
              <div className="alert alert-warning" style={{ marginBottom: '1rem' }}>
                {t('account.contact_incomplete_warning')}
              </div>
            )}
            <FormLegend />
            <div className="form-group">
              <label>
                {t('account.location_section')}{' '}
                <Tooltip content={t('account.location_section_help')} />
              </label>
            </div>
            <div className={`form-group${addressError ? ' form-group--error' : ''}`}>
              <FormLabel required>{t('account.precise_address_street')}</FormLabel>
              <input
                type="text"
                placeholder={t('account.precise_address_street_placeholder')}
                value={street}
                onChange={e => { setStreet(e.target.value); setAddressError(''); }}
                required
                aria-required="true"
                aria-invalid={addressError ? true : undefined}
              />
            </div>
            <div className={`form-group${addressError ? ' form-group--error' : ''}`}>
              <FormLabel required>{t('account.precise_address_house_number')}</FormLabel>
              <input
                type="text"
                placeholder={t('account.precise_address_house_number_placeholder')}
                value={houseNumber}
                onChange={e => { setHouseNumber(e.target.value); setAddressError(''); }}
                required
                aria-required="true"
                aria-invalid={addressError ? true : undefined}
              />
            </div>
            <div className={`form-group${addressError ? ' form-group--error' : ''}`}>
              <FormLabel required>{t('account.precise_address_city')}</FormLabel>
              <input
                type="text"
                placeholder={t('account.precise_address_city_placeholder')}
                value={city}
                onChange={e => { setCity(e.target.value); setAddressError(''); }}
                required
                aria-required="true"
                aria-invalid={addressError ? true : undefined}
              />
            </div>
            <div className={`form-group${addressError ? ' form-group--error' : ''}`}>
              <FormLabel required>{t('account.precise_address_country')}</FormLabel>
              <select
                value={country}
                onChange={e => { setCountry(e.target.value); setAddressError(''); }}
                required
                aria-required="true"
                aria-invalid={addressError ? true : undefined}
                aria-describedby={addressError ? 'account-address-error' : undefined}
              >
                {COUNTRY_CODES.map(code => (
                  <option key={code} value={code}>{t(countryLabelKey(code))}</option>
                ))}
              </select>
              <FieldError id="account-address-error" message={addressError} />
            </div>
            {geocodeStatus === 'loading' && (
              <p className="text-muted" style={{ fontSize: '0.9rem' }}>{t('account.validating_address')}</p>
            )}
            {geocodeStatus === 'error' && geocodeError && (
              <p style={{ fontSize: '0.9rem', color: 'var(--danger, #c00)' }}>{geocodeError}</p>
            )}
            {showPreviewMap && (
              <div className="form-group">
                <label>{t('account.location_preview')}</label>
                <LocationMap precise={previewPrecise} height={240} />
              </div>
            )}
            {showSavedMap && (
              <div className="form-group">
                <label>{t('account.location_saved')}</label>
                <LocationMap precise={savedPrecise} approximate={savedApproximate} height={280} />
              </div>
            )}
            <div className="form-actions" style={{ marginTop: '1rem' }}>
              <button type="submit" className="btn btn-primary">
                {t('account.settings_save')}
              </button>
            </div>
          </div>
          {(
            (connectStatus === null || !connectStatus.selfBillingAccepted) &&
              <SelfBillingAcceptance onAccepted={async () => {
                try {
                  const res = await api.get<ConnectStatus>('/connect/status');
                  setConnectStatus(res.data);
                  await fetchOfferEligibility();
                  onOfferEligibilityChange?.();
                  setMessage(t('self_billing.accepted'));
                  setMessageType('success');
                } catch {
                  // ignore refresh failure
                }
              }} />
          )}
          <PayoutSetupSection
              setError={setError}
              onStatusLoaded={setConnectStatus}
              beforeRedirect={confirmDiscard}
              startBlockedMessage={payoutStartBlockedMessage}
          />
        </form>
      )}

      {tab === 'availability' && (
        <div>
          <OwnerUnavailablePeriods setError={setError} />
        </div>
      )}

      <Modal
        open={confirmOpen}
        onClose={cancelLeave}
        title={t('account.unsaved_title')}
        showCloseButton={false}
      >
        <p>{t('account.unsaved_message')}</p>
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={cancelLeave}>
            {t('account.unsaved_stay')}
          </button>
          <button type="button" className="btn btn-danger" onClick={confirmLeave}>
            {t('account.unsaved_leave')}
          </button>
        </div>
      </Modal>
    </div>
  );
};
